import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context';
import { AdminLayout } from '../components/layout';
import {
  LoginPage,
  DashboardPage,
  NewsListPage,
  NewsFormPage,
  BlogListPage,
  BlogFormPage,
  GalleryPage,
  ClientsPage,
  ServicesPage,
  ReviewsPage,
  CareersPage,
  CareerFormPage,
  CareerApplicationsPage,
  TeamMembersPage,
  PagesListPage,
  PageEditorPage,
  SiteSettingsPage,
  MediaLibraryPage,
  BillingClientsPage,
  BillingClientDetailPage,
  ClientImportPage,
  InvoicesListPage,
  InvoiceEditorPage,
  InvoiceDetailPage,
  BillingSettingsPage
} from '../pages';
import { Spinner } from '../components/common';

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="route-loading">
        <Spinner size="large" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Wrapper (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="route-loading">
        <Spinner size="large" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        
        {/* News Management */}
        <Route path="news" element={<NewsListPage />} />
        <Route path="news/add" element={<NewsFormPage />} />
        <Route path="news/edit/:slug" element={<NewsFormPage />} />
        
        {/* Blog Management */}
        <Route path="blogs" element={<BlogListPage />} />
        <Route path="blogs/add" element={<BlogFormPage />} />
        <Route path="blogs/edit/:slug" element={<BlogFormPage />} />
        
        {/* Gallery Management */}
        <Route path="gallery" element={<GalleryPage />} />
        
        {/* Clients Management */}
        <Route path="clients" element={<ClientsPage />} />
        
        {/* Services Management */}
        <Route path="services" element={<ServicesPage />} />
        
        {/* Reviews Management */}
        <Route path="reviews" element={<ReviewsPage />} />
        
        {/* Careers Management */}
        <Route path="careers" element={<CareersPage />} />
        <Route path="careers/add" element={<CareerFormPage />} />
        <Route path="careers/edit/:id" element={<CareerFormPage />} />
        <Route path="careers/applications" element={<CareerApplicationsPage />} />

        {/* Team Management */}
        <Route path="team" element={<TeamMembersPage />} />

        {/* CMS — website content */}
        <Route path="cms/pages" element={<PagesListPage />} />
        <Route path="cms/pages/:id" element={<PageEditorPage />} />
        <Route path="cms/media" element={<MediaLibraryPage />} />
        <Route path="cms/settings" element={<SiteSettingsPage />} />

        {/* Billing — tax invoices (admin only; unrelated to the logo `clients` above) */}
        <Route path="billing/clients" element={<BillingClientsPage />} />
        <Route path="billing/clients/import" element={<ClientImportPage />} />
        <Route path="billing/clients/:id" element={<BillingClientDetailPage />} />
        <Route path="billing/invoices" element={<InvoicesListPage />} />
        <Route path="billing/invoices/new" element={<InvoiceEditorPage />} />
        <Route path="billing/invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="billing/invoices/:id/edit" element={<InvoiceEditorPage />} />
        <Route path="billing/settings" element={<BillingSettingsPage />} />
      </Route>

      {/* 404 - Redirect to Dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
